import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMessageCircle, FiClock } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { CATEGORIES, REACTIONS } from '../../utils/constants';
import { reactionsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { resolveMediaUrl } from '../../utils/media';
import toast from 'react-hot-toast';
import './PostListView.css';

const PostListView = ({ post }) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const category = CATEGORIES.find(cat => cat.id === post.category);

  const isVideo = post?.type === 'video-horizontal' || post?.type === 'video-reel';
  const isMeme = post?.type === 'meme';

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getPostSlug = () => {
    if (post?.slug) return post.slug;
    const base = slugify(post?.title) || 'post';
    return post?.id ? `${base}-${post.id}` : base;
  };

  const getPostRoute = () => {
    const slug = getPostSlug();
    if (post?.type === 'meme') return `/meme/${slug}`;
    if (post?.type === 'video-horizontal' || post?.type === 'video-reel') return `/video/${slug}`;
    return `/article/${slug}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString();
  };

  const initialReactions = useMemo(() => {
    if (post?.reactions && typeof post.reactions === 'object') return post.reactions;
    return {};
  }, [post?.reactions]);

  const [reactionCounts, setReactionCounts] = useState(initialReactions);
  const [userReaction, setUserReaction] = useState(null);

  useEffect(() => {
    setReactionCounts(initialReactions);
    setUserReaction(null);
  }, [post?.id, initialReactions]);

  const handleReaction = async (reactionId) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    try {
      const res = await reactionsAPI.toggle(post.id, reactionId);
      setUserReaction(res.data?.userReaction || null);
      setReactionCounts(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
      toast.error('No se pudo reaccionar');
    }
  };

  const author = post?.author || {};
  const authorIsVerified = Boolean(author?.verification_level && author.verification_level !== 'none');
  const createdAt = post?.createdAt || post?.created_at;
  const commentsCount = post?.comments ?? post?.commentsCount ?? post?.comments_count ?? 0;
  const imageUrl = resolveMediaUrl(post?.image || post?.cover_image);
  const videoUrl = resolveMediaUrl(post?.video_url || post?.videoUrl);
  const linkState = isMeme ? undefined : { backgroundLocation: location };

  return (
    <article className="post-list-view">
      <Link to={getPostRoute()} state={linkState} className="post-list-image">
        {isVideo && !imageUrl && videoUrl ? (
          <video
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => {
              try {
                e.currentTarget.currentTime = 0.1;
              } catch (_) {
                // ignore
              }
            }}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <img src={imageUrl} alt={post.title} loading="lazy" />
        )}
      </Link>

      <div className="post-list-content">
        <div className="post-list-header">
          {category && (
            <span className="post-list-category" style={{ color: category.color }}>
              {category.icon} {category.name}
            </span>
          )}
        </div>

        <Link to={getPostRoute()} state={linkState} className="post-list-title-link">
          <h3 className="post-list-title">{post.title}</h3>
        </Link>

        <div className="post-list-reactions" aria-label="Reacciones">
          {REACTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`post-list-reaction-btn ${userReaction === r.id ? 'active' : ''}`}
              onClick={() => handleReaction(r.id)}
              title={r.label}
            >
              <span className="post-list-reaction-emoji">{r.emoji}</span>
              <span className="post-list-reaction-count">{reactionCounts?.[r.id] || 0}</span>
            </button>
          ))}
        </div>

        <div className="post-list-meta">
          <div className="post-list-author">
            <img src={resolveMediaUrl(author?.avatar) || '/default-avatar.png'} alt={author?.name || 'Autor'} />
            <span>
              {author?.name || 'Usuario'}
              {authorIsVerified && (
                <MdVerified
                  style={{
                    marginLeft: 6,
                    verticalAlign: 'middle',
                    color: author.badge_color || 'currentColor'
                  }}
                  title={author.badge_label || 'Cuenta verificada'}
                />
              )}
            </span>
          </div>
          <div className="post-list-stats">
            <span><FiClock /> {createdAt ? formatDate(createdAt) : '—'}</span>
            <span><FiMessageCircle /> {commentsCount}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

export default PostListView;
